import { cn } from "../cn";

export interface RankProps {
  /**
   * The position on the board, or `null` for no position at all.
   *
   * `null` is the case this component exists to make unmissable: it means the
   * player has no score here, and it is **not last place**. A number is the only
   * thing that can be rendered as a position, so the absence has to be its own
   * value rather than a zero or a sentinel somebody formats by accident.
   */
  place: number | null;
  /**
   * False when `place` is a floor rather than the number.
   *
   * A rank is a count of everybody ahead, and the platform stops counting at the
   * deepest position anybody looks at. Past that bound the number means "no
   * better than this", and rendering it plainly tells somebody they are exactly
   * ten-thousandth of a million when nobody ever counted that far.
   */
  exact?: boolean;
  /** Larger, for a board being read as a board rather than as a row on a list. */
  size?: "sm" | "md";
  className?: string;
}

/**
 * A place on a leaderboard — the one way a rank reaches a screen.
 *
 * It is a primitive rather than a helper on the screen that needed it first,
 * because three things about a rank are got wrong by default and the default is
 * what a second implementation reverts to: a missing place rendered as last, a
 * floor rendered as a position, and digits that do not line up down a column. A
 * component nobody can route around is the only version of that rule which
 * survives the next screen.
 *
 * The top places are heavier than the rest, and nothing below them carries a
 * badge. A medal on every row is a table with rounder corners; the point of the
 * chip is that the eye finds the head of the board without reading, and a chip
 * on row forty says nothing at all.
 */
export function Rank({ place, exact = true, size = "sm", className }: RankProps) {
  const big = size === "md";

  if (place === null) {
    return (
      <span
        className={cn(
          "inline-flex shrink-0 items-center justify-center rounded-full",
          "border border-dashed border-rule-strong text-ink-3",
          big ? "size-9 text-ui" : "size-7 text-micro",
          className,
        )}
      >
        <span aria-hidden="true">–</span>
        <span className="sr-only">No place on this board</span>
      </span>
    );
  }

  // Only the head of the board is decorated, and only when the number is the
  // number. A floor is never in the top three, so the two cases cannot collide.
  if (exact && place <= 3) {
    return (
      <span
        className={cn(
          "inline-flex shrink-0 items-center justify-center rounded-full font-semibold tabular-nums",
          // An ink ramp rather than a colour: first place is the strongest mark
          // on the row and the two behind it step back from it. Both ends are
          // tokens, so the chip inverts with the theme instead of being a light
          // theme drawn twice.
          place === 1 && "bg-ink text-ground",
          place === 2 && "bg-ink-2 text-ground",
          place === 3 && "bg-ink-3 text-ground",
          big ? "size-9 text-prose" : "size-7 text-ui",
          className,
        )}
      >
        <span className="sr-only">Place </span>
        {place}
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex shrink-0 flex-col items-center leading-tight text-ink-2",
        big ? "min-w-9 text-ui" : "min-w-7 text-micro",
        className,
      )}
    >
      <span className="font-medium tabular-nums">
        <span className="sr-only">Place </span>
        {place.toLocaleString()}
      </span>
      {/*
        Said on the place itself rather than beside it, because the qualifier
        travelling separately is exactly how it gets dropped by the next screen
        to render one of these.
      */}
      {exact ? null : <span className="text-micro text-ink-3">or lower</span>}
    </span>
  );
}
